<template>
  <nav class="bg-white shadow-md sticky top-0 z-50">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex justify-between h-16">
        <!-- Logo & Desktop Menu -->
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <img class="h-12 w-auto" src="@/assets/mrj1.png" alt="Research Portal Logo">
          </div>
          <div class="hidden sm:ml-6 sm:flex sm:space-x-8">
            <RouterLink 
              to="/" 
              class="nav-link" 
              :class="{ 'nav-active': $route.name === 'home' }"
            >Home</RouterLink>

            <RouterLink 
              to="/browse" 
              class="nav-link" 
              :class="{ 'nav-active': $route.name === 'browse' }"
            >Browse Journals</RouterLink>

            <RouterLink 
              to="/submit" 
              class="nav-link" 
              :class="{ 'nav-active': $route.name === 'submit' }"
            >Submit Research</RouterLink>

            <RouterLink 
              v-if="isLoggedIn"
              :to="user.role === 'admin' ? '/adminview' : '/submission'"
              class="nav-link"
              :class="{
                'nav-active': $route.name === (user.role === 'admin' ? 'adminview' : 'submission')
              }"
            >
              {{
                user.role === 'researcher'
                  ? 'My Submissions'
                  : user.role === 'admin'
                    ? 'Admin'
                    : 'Manage Submissions'
              }}
            </RouterLink>

            <RouterLink 
              to="/about" 
              class="nav-link" 
              :class="{ 'nav-active': $route.name === 'about' }"
            >About</RouterLink>
          </div>
        </div>

        <!-- Desktop Search & User Menu -->
        <div class="hidden sm:flex sm:items-center">
          <div class="relative mx-4">
            <input 
              type="text" 
              :value="searchQuery"
              @input="$emit('update-search', $event.target.value)"
              @keyup.enter="$emit('perform-search')" 
              placeholder="Search articles..." 
              class="pl-10 pr-4 py-2 border rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 w-64"
            >
            <i class="fas fa-search absolute left-3 top-2.5 text-gray-400"></i>
          </div>

          <!-- Sign In -->
          <button 
            v-if="!isLoggedIn" 
            @click="$emit('show-login')" 
            class="ml-4 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md"
          >
            Sign In
          </button>

          <!-- User Avatar -->
          <div v-else class="ml-4 relative">
            <button @click="showUserDropdown = !showUserDropdown" class="flex items-center text-sm rounded-full focus:outline-none">
              <img class="h-8 w-8 rounded-full" :src="user.avatar" :alt="user.name">
              <span class="ml-2 text-gray-700">{{ user.name }}</span>
            </button>
          <transition name="fade">
  <div
    v-if="showUserDropdown"
    class="absolute right-0 mt-2 w-56 rounded-xl shadow-lg bg-white z-50 ring-1 ring-black/10 overflow-hidden"
  >
    <nav class="flex flex-col">
      <!-- Dashboard -->
      <RouterLink
        to="/author-profile"
        class="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors"
      >
        Dashboard
      </RouterLink>

      <!-- Submissions (Dynamic Label) -->
      <RouterLink
        :to="user.role === 'admin' ? '/adminview' : '/submission'"
        class="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors"
      >
        {{ user.role === 'researcher' ? 'My Submissions' : 'Manage Submissions' }}
      </RouterLink>

      <!-- Settings -->
      <RouterLink
        to="/settings"
        class="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors"
      >
        Settings
      </RouterLink>

      <!-- Divider -->
      <div class="border-t border-gray-200 my-1"></div>

      <!-- Sign out -->
      <a
        href="#"
        @click.prevent="$emit('logout')"
        class="px-4 py-2 text-sm text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors"
      >
        Sign out
      </a>
    </nav>
  </div>
</transition>

          </div>
        </div>

        <!-- Mobile Menu Button -->
        <div class="-mr-2 flex sm:hidden">
          <button @click="mobileMenuOpen = !mobileMenuOpen" class="p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded-md">
            <i :class="mobileMenuOpen ? 'fas fa-times' : 'fas fa-bars'" class="h-6 w-6"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Mobile Menu -->
    <transition name="fade">
      <div v-if="mobileMenuOpen" class="sm:hidden px-4 pt-2 pb-3 space-y-2">
        <RouterLink to="/" class="mobile-link" :class="{ 'mobile-active': $route.name === 'home' }">Home</RouterLink>
        <RouterLink to="/browse" class="mobile-link" :class="{ 'mobile-active': $route.name === 'browse' }">Browse Journals</RouterLink>
        <RouterLink to="/submit" class="mobile-link" :class="{ 'mobile-active': $route.name === 'submit' }">Submit Research</RouterLink>
        <RouterLink 
          v-if="isLoggedIn" 
          :to="user.role === 'admin' ? '/adminview' : '/submission'" 
          class="mobile-link"
          :class="{ 'mobile-active': $route.name === (user.role === 'admin' ? 'adminview' : 'submission') }"
        >
          {{ user.role === 'researcher' ? 'My Submissions' : 'Manage Submissions' }}
        </RouterLink>
        <RouterLink to="/about" class="mobile-link" :class="{ 'mobile-active': $route.name === 'about' }">About</RouterLink>

        <!-- Mobile Search and Auth -->
        <div class="pt-4">
          <div class="relative mb-2">
            <input 
              type="text" 
              :value="searchQuery"
              @input="$emit('update-search', $event.target.value)"
              @keyup.enter="$emit('perform-search')" 
              placeholder="Search articles..." 
              class="pl-10 pr-4 py-2 border rounded-full text-sm w-full focus:outline-none focus:ring-2 focus:ring-green-500"
            >
            <i class="fas fa-search absolute left-3 top-2.5 text-gray-400"></i>
          </div>

          <button 
            v-if="!isLoggedIn" 
            @click="$emit('show-login'); mobileMenuOpen = false" 
            class="w-full text-sm text-white bg-green-600 hover:bg-green-700 px-4 py-2 rounded-md"
          >
            Sign In
          </button>

          <div v-else class="flex items-center justify-between">
            <div class="flex items-center space-x-2">
              <img class="h-8 w-8 rounded-full" :src="user.avatar" :alt="user.name">
              <span class="text-gray-700">{{ user.name }}</span>
            </div>
            <button @click="$emit('logout')" class="text-green-600 hover:text-green-800 text-sm">Sign out</button>
          </div>
        </div>
      </div>
    </transition>
  </nav>
</template>

<script>
import { ref } from 'vue'
import { RouterLink, useRoute } from 'vue-router'

export default {
  name: 'NavBar',
  components: { RouterLink },
  props: {
    isLoggedIn: Boolean,
    user: Object,
    searchQuery: String
  },
  emits: ['update-search', 'perform-search', 'show-login', 'logout'],
  setup() {
    const mobileMenuOpen = ref(false)
    const showUserDropdown = ref(false)
    const route = useRoute()

    return {
      mobileMenuOpen,
      showUserDropdown,
      route
    }
  }
}
</script>

<style scoped>
.nav-link {
  @apply inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300;
}
.nav-active {
  @apply border-green-500 text-gray-900;
}
.dropdown-item {
  @apply block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100;
}
.mobile-link {
  @apply block pl-3 pr-4 py-2 border-l-4 text-base font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-700 hover:border-gray-300;
}
.mobile-active {
  @apply bg-green-50 border-green-500 text-green-700;
}
</style>
